import { apiClient } from '../apiClient';

import { emptyEvidenceCounts } from './readiness';

import type {
  ReadinessBreakdown,
  ReadinessComponentEvidencePage,
  ReadinessEvidence,
  ReadinessEvidenceItem,
  ReadinessEvidenceStatus,
} from './readiness';

export type ReadinessBand = 'product_ready' | 'service_ready' | 'developing' | 'needs_support';

/** Filters the paged evidence endpoint understands. All optional. */
export interface OrgReadinessEvidenceQuery {
  status?: ReadinessEvidenceStatus;
  /** Case-insensitive substring, matched against label OR detail by the API. */
  q?: string;
  limit?: number;
  offset?: number;
}

export interface OrgTrainer {
  user_id: string;
  name: string;
}

export interface OrgBatchSummary {
  id: string;
  name: string;
  status: 'active' | 'archived';
  start_date?: string;
  end_date?: string;
  student_count: number;
  avg_completion_pct: number;
  trainers: OrgTrainer[];
  pending_trainer_reply: boolean;
}

export interface OrgOverview {
  org_name: string;
  total_batches: number;
  active_batches: number;
  archived_batches: number;
  total_students: number;
  total_trainers: number;
  avg_completion_pct: number;
  readiness_distribution: Record<ReadinessBand, number>;
}

export interface OrgStudentRow {
  user_id: string;
  name: string;
  email: string;
  completion_pct: number;
  avg_score: number;
  attendance_pct: number;
  tier: string;
  readiness_band: ReadinessBand;
  streak_days: number;
  enrollment_status: string;
  enrolled_at?: string;
  completed_topics: number;
  in_progress_topics: number;
  total_topics: number;
}

export interface CompletionSummary {
  not_started: number;
  just_started: number;
  in_progress: number;
  advanced: number;
  completed: number;
}

export interface OrgBatchDetail {
  batch: OrgBatchSummary;
  total_topics: number;
  students: OrgStudentRow[];
  summary: CompletionSummary;
}

export interface OrgTopicProgress {
  topic_id: string;
  title: string;
  status: string;
  completed_at?: string;
}

export interface OrgAssessmentRow {
  title: string;
  type: string;
  status: string;
  score: number;
  max_score: number;
  submitted_at?: string;
}

export interface OrgBatchAssignmentRow {
  assignment_id: string;
  title: string;
  kind: 'assignment' | 'project';
  due_date?: string;
  submitted_count: number;
  total_students: number;
  avg_score: number;
  max_score: number;
  overdue: boolean;
}

export interface OrgAssignmentSubmissionRow {
  user_id: string;
  name: string;
  email: string;
  status: 'submitted' | 'pending' | 'late';
  score: number;
  submitted_at?: string;
}

export interface OrgAssignmentDetail {
  assignment_id: string;
  title: string;
  kind: 'assignment' | 'project';
  due_date?: string;
  max_score: number;
  submissions: OrgAssignmentSubmissionRow[];
}

export interface OrgBatchAssessmentRow {
  assessment_id: string;
  title: string;
  kind: 'mcq_quiz' | 'coding_challenge';
  due_date?: string;
  submitted_count: number;
  total_students: number;
  avg_score_pct: number;
  max_score: number;
  overdue: boolean;
}

export interface OrgAssessmentSubmissionRow {
  user_id: string;
  name: string;
  email: string;
  status: 'submitted' | 'graded' | 'returned' | 'resubmitted' | 'not_submitted';
  type?: 'mcq_response' | 'code_submission';
  score: number;
  submitted_at?: string;
}

export interface OrgBatchAssessmentDetail {
  assessment_id: string;
  title: string;
  kind: 'mcq_quiz' | 'coding_challenge';
  due_date?: string;
  max_score: number;
  submissions: OrgAssessmentSubmissionRow[];
}

export interface OrgBatchSessionRow {
  session_id: string;
  title: string;
  session_type: 'live_class' | 'recorded' | 'office_hours' | 'lab';
  scheduled_at?: string;
  status: 'scheduled' | 'live' | 'completed' | 'cancelled';
  present_count: number;
  total_students: number;
}

export interface OrgSessionAttendanceRow {
  user_id: string;
  name: string;
  email: string;
  status: 'present' | 'absent' | 'late' | 'not_marked';
  joined_at?: string;
}

export interface OrgSessionDetail {
  session_id: string;
  title: string;
  session_type: 'live_class' | 'recorded' | 'office_hours' | 'lab';
  scheduled_at?: string;
  status: 'scheduled' | 'live' | 'completed' | 'cancelled';
  attendance: OrgSessionAttendanceRow[];
}

export interface OrgStudentDetail {
  user_id: string;
  name: string;
  email: string;
  batch_id: string;
  batch_name: string;
  completion_pct: number;
  avg_score: number;
  attendance_pct: number;
  on_time_rate: number;
  readiness_score: number;
  tier: string;
  readiness_band: ReadinessBand;
  streak_days: number;
  skill_scores: Record<string, number>;
  strengths: string[];
  gaps: string[];
  topics: OrgTopicProgress[];
  assessments: OrgAssessmentRow[];
  last_active: string | null;
  at_risk_reasons: string[];
  batch_rank: number;
  batch_size: number;
  percentile: number;
  benchmarks: {
    completion_median: number;
    avg_score_median: number;
    attendance_median: number;
    readiness_median: number;
  };
  skill_medians: Record<string, number>;
  verdict: string;
  notes: { id: string; content: string; created_by: string; created_at: string }[];
  shortlisted: boolean;
  flagged: boolean;
  trends?: {
    avg_score: { week: string; value: number }[];
    activity: { week: string; value: number }[];
    completion: { week: string; value: number }[];
  };
  /**
   * Readiness explainability. The org portal used to show a bare score with a
   * one-line description that did not match the formula; these carry the real
   * per-component basis so a TPO can justify the number to a student.
   */
  breakdown?: ReadinessBreakdown;
  formula_version?: number;
  data_coverage?: number;
  /** ISO-8601; when this score was last computed. */
  computed_at?: string;
}

export interface OrgStudentListItem {
  user_id: string;
  name: string;
  email: string;
  batch_id: string;
  batch_name: string;
  readiness_band: ReadinessBand;
  readiness_score: number;
  tier: string;
  completion_pct: number;
  avg_score: number;
  skill_scores: Record<string, number>;
  /** Share of formula weight that had data behind this score, 0..1. */
  data_coverage: number;
  /** ISO-8601; absent when the score has never been computed. */
  computed_at?: string;
}

export interface OrgInsights {
  north_star: { ready_pct: number; ready_count: number; active_count: number; target_pct: number };
  weekly_active: { pct: number; active_count?: number; total_count?: number };
  coverage: { pct: number; covered?: number; total?: number };
  tier_counts: { product_ready: number; service_ready: number; developing: number; needs_support: number };
  skill_gap: { skill: string; strong: number; ok: number; weak: number; critical: number; total: number }[];
  at_risk: { user_id: string; name: string; batch_id: string; batch_name: string; readiness_score: number; reasons: string[] }[];
  batches: { batch_id: string; name: string; student_count: number; avg_completion_pct: number; avg_readiness: number; weekly_active_pct: number }[];
  attention: { severity: 'risk' | 'watch'; kind: string; text: string; batch_id?: string }[];
}

export interface OrgTrendPoint {
  week: string;
  value: number;
}

export interface OrgTrendSeries {
  series: OrgTrendPoint[];
  delta_pct: number;
}

export interface OrgBatchTrend {
  batch_id: string;
  series: OrgTrendPoint[];
}

export interface OrgTrends {
  weekly_active: OrgTrendSeries;
  coverage: OrgTrendSeries;
  batches: OrgBatchTrend[];
}

export const orgApi = {
  getOverview: async (): Promise<OrgOverview> => {
    const { data } = await apiClient.get('/api/v1/org/overview');
    return data as OrgOverview;
  },
  getInsights: async (): Promise<OrgInsights> => {
    const { data } = await apiClient.get('/api/v1/org/insights');
    return data as OrgInsights;
  },
  getTrends: async (): Promise<OrgTrends> => {
    const { data } = await apiClient.get('/api/v1/org/trends');
    return data as OrgTrends;
  },
  getBatches: async (): Promise<OrgBatchSummary[]> => {
    const { data } = await apiClient.get('/api/v1/org/batches');
    return (data?.batches as OrgBatchSummary[]) ?? [];
  },
  getBatchDetail: async (batchId: string): Promise<OrgBatchDetail> => {
    const { data } = await apiClient.get(`/api/v1/org/batches/${batchId}`);
    return data as OrgBatchDetail;
  },
  getStudentDetail: async (batchId: string, userId: string): Promise<OrgStudentDetail> => {
    const { data } = await apiClient.get(
      `/api/v1/org/batches/${batchId}/students/${userId}?include_trends=true`,
    );
    return data as OrgStudentDetail;
  },
  /**
   * The shape of every readiness component, plus a handful of worst-first
   * examples each, for one student.
   *
   * Constant-size whatever the batch looks like: one request serves all seven
   * components and none of them sends more than a preview. The full list of a
   * component's records is a separate, paged request — see below.
   */
  getStudentReadinessEvidence: async (
    batchId: string,
    userId: string,
  ): Promise<ReadinessEvidence> => {
    const { data } = await apiClient.get(
      `/api/v1/org/batches/${batchId}/students/${userId}/readiness-evidence`,
    );
    return { components: (data?.components ?? {}) as ReadinessEvidence['components'] };
  },
  /**
   * One component's records, filtered and paged — what the drill-down drawer
   * walks. Deliberately never fetched to render the card: a 327-topic
   * curriculum is not something to ship in order to draw a bar.
   */
  getStudentReadinessComponentEvidence: async (
    batchId: string,
    userId: string,
    component: string,
    params: OrgReadinessEvidenceQuery = {},
  ): Promise<ReadinessComponentEvidencePage> => {
    const { data } = await apiClient.get(
      `/api/v1/org/batches/${batchId}/students/${userId}/readiness-evidence/${component}`,
      {
        params: {
          // Omitted rather than sent empty: `status=` would be a filter that
          // matches nothing on a stricter server.
          ...(params.status ? { status: params.status } : {}),
          ...(params.q ? { q: params.q } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
          ...(params.offset ? { offset: params.offset } : {}),
        },
      },
    );
    const page = (data ?? {}) as Partial<ReadinessComponentEvidencePage>;
    return {
      component: page.component ?? component,
      summary: page.summary ?? '',
      counts: page.counts ?? emptyEvidenceCounts(),
      // The list is the one field a missing value would crash on.
      items: (page.items ?? []) as ReadinessEvidenceItem[],
      total: page.total ?? 0,
      limit: page.limit ?? params.limit ?? 0,
      offset: page.offset ?? params.offset ?? 0,
    };
  },
  getBatchAssignments: async (batchId: string): Promise<OrgBatchAssignmentRow[]> => {
    const { data } = await apiClient.get(`/api/v1/org/batches/${batchId}/assignments`);
    return (data?.assignments as OrgBatchAssignmentRow[]) ?? [];
  },
  getAssignmentDetail: async (batchId: string, assignmentId: string): Promise<OrgAssignmentDetail> => {
    const { data } = await apiClient.get(`/api/v1/org/batches/${batchId}/assignments/${assignmentId}`);
    return data as OrgAssignmentDetail;
  },
  getBatchAssessments: async (batchId: string): Promise<OrgBatchAssessmentRow[]> => {
    const { data } = await apiClient.get(`/api/v1/org/batches/${batchId}/assessments`);
    return (data?.assessments as OrgBatchAssessmentRow[]) ?? [];
  },
  getAssessmentDetail: async (batchId: string, assessmentId: string): Promise<OrgBatchAssessmentDetail> => {
    const { data } = await apiClient.get(`/api/v1/org/batches/${batchId}/assessments/${assessmentId}`);
    return data as OrgBatchAssessmentDetail;
  },
  getBatchSessions: async (batchId: string): Promise<OrgBatchSessionRow[]> => {
    const { data } = await apiClient.get(`/api/v1/org/batches/${batchId}/sessions`);
    return (data?.sessions as OrgBatchSessionRow[]) ?? [];
  },
  getSessionDetail: async (batchId: string, sessionId: string): Promise<OrgSessionDetail> => {
    const { data } = await apiClient.get(`/api/v1/org/batches/${batchId}/sessions/${sessionId}`);
    return data as OrgSessionDetail;
  },
  getStudents: async (): Promise<OrgStudentListItem[]> => {
    const { data } = await apiClient.get('/api/v1/org/students');
    return (data?.students as OrgStudentListItem[]) ?? [];
  },
  createNote: async (batchId: string, userId: string, content: string) => {
    const { data } = await apiClient.post(
      `/api/v1/org/batches/${batchId}/students/${userId}/notes`,
      { content },
    );
    return data;
  },
  setShortlist: async (batchId: string, userId: string, on: boolean) => {
    const url = `/api/v1/org/batches/${batchId}/students/${userId}/shortlist`;
    return on ? apiClient.post(url) : apiClient.delete(url);
  },
  setFlag: async (batchId: string, userId: string, on: boolean) => {
    const url = `/api/v1/org/batches/${batchId}/students/${userId}/flag`;
    return on ? apiClient.post(url) : apiClient.delete(url);
  },

  // --- management (batch / enrollment / trainer) ---
  createBatch: async (body: OrgBatchInput): Promise<OrgBatchWrite> => {
    const { data } = await apiClient.post('/api/v1/org/batches', body);
    return data as OrgBatchWrite;
  },
  updateBatch: async (batchId: string, body: OrgBatchInput): Promise<OrgBatchWrite> => {
    const { data } = await apiClient.patch(`/api/v1/org/batches/${batchId}`, body);
    return data as OrgBatchWrite;
  },
  archiveBatch: async (batchId: string) => apiClient.post(`/api/v1/org/batches/${batchId}/archive`),
  getAssignableStudents: async (batchId: string): Promise<OrgAssignableUser[]> => {
    const { data } = await apiClient.get(`/api/v1/org/batches/${batchId}/assignable-students`);
    return (data?.students as OrgAssignableUser[]) ?? [];
  },
  enrollStudent: async (batchId: string, userId: string) =>
    apiClient.post(`/api/v1/org/batches/${batchId}/students`, { user_id: userId }),
  removeStudent: async (batchId: string, userId: string) =>
    apiClient.delete(`/api/v1/org/batches/${batchId}/students/${userId}`),
  getOrgTrainers: async (): Promise<OrgAssignableUser[]> => {
    const { data } = await apiClient.get('/api/v1/org/trainers');
    return (data?.trainers as OrgAssignableUser[]) ?? [];
  },
  assignTrainer: async (batchId: string, userId: string) =>
    apiClient.post(`/api/v1/org/batches/${batchId}/trainers`, { user_id: userId }),
  removeTrainer: async (batchId: string, userId: string) =>
    apiClient.delete(`/api/v1/org/batches/${batchId}/trainers/${userId}`),

  getOrgProfile: async (): Promise<OrgProfile> => {
    const { data } = await apiClient.get('/api/v1/org/profile');
    return data as OrgProfile;
  },
  updateOrgName: async (name: string): Promise<{ name: string }> => {
    const { data } = await apiClient.patch('/api/v1/org/profile', { name });
    return data as { name: string };
  },

  getCompanyReadinessCompanies: async (): Promise<OrgCompanyCount[]> => {
    const { data } = await apiClient.get('/api/v1/org/company-readiness/companies');
    return (data as OrgCompanyCount[]) ?? [];
  },
  getCompanyReadiness: async (company: string, batchId?: string): Promise<OrgCompanyReadiness> => {
    const { data } = await apiClient.get('/api/v1/org/company-readiness', {
      params: { company, batch_id: batchId || undefined },
    });
    return data as OrgCompanyReadiness;
  },
  getStudentCompanyFit: async (userId: string): Promise<OrgCompanyFitRow[]> => {
    const { data } = await apiClient.get(`/api/v1/org/company-readiness/student/${userId}`);
    return (data?.companies as OrgCompanyFitRow[]) ?? [];
  },
};

export interface OrgCompanyCount {
  company: string;
  problem_count: number;
}

export interface OrgCompanyFitRow {
  company: string;
  solved: number;
  total: number;
  pct: number;
  band: 'ready' | 'developing' | 'needs_practice';
}

export interface OrgCompanyReadinessRow {
  user_id: string;
  name: string;
  email: string;
  batch_id: string;
  solved: number;
  total: number;
  pct: number;
  band: 'ready' | 'developing' | 'needs_practice';
}

export interface OrgCompanyReadiness {
  company: string;
  total: number;
  students: OrgCompanyReadinessRow[];
}

export interface OrgProfile {
  id: string;
  name: string;
  slug: string;
  logo_url: string;
  created_at: string;
}

export interface OrgBatchInput {
  name?: string;
  start_date?: string;
  end_date?: string;
}
export interface OrgBatchWrite {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  archived_at: string;
}
export interface OrgAssignableUser {
  user_id: string;
  name: string;
  email: string;
}
